import {
  BorderStyle,
  Document,
  ImageRun,
  Packer,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  WidthType,
  TextRun
} from 'docx';
import { ParsedTableData, getSourcesList, prepareContentForTable } from '../ChatReply/ChatReply';
import { downloadFilenameGenerator, sanitizeHeaderText } from '.';
import LABELS from '../../../ui-constants/en.json';
import AppConst from '../../../ui-constants/appConstants.json';
import { publicSources } from '../../../label.constant';
import { decodeHTML, getResponseSourceType } from '../../../utils/commonUtils';

import {
  IQuery,
  IQueryContent,
  RESPONSE_BY,
  RESPONSE_TYPE,
  ItableCntentProps,
} from "../../../models/IQueryType";

const noBorder = {
  style: BorderStyle.NONE,
  size: 0,
  color: "ff0000",
};
const singleBorder = {
  style: BorderStyle.SINGLE,
  size: 1,
  color: "e8e8ee",
};
const noBorders = {
  top: noBorder,
  bottom: noBorder,
  left: noBorder,
  right: noBorder,
};

const tableCellStyling = {
  borders: {
    top: singleBorder,
    bottom: singleBorder,
    left: singleBorder,
    right: singleBorder,
  },
  shading: {
    color: "f7f7f8",
    fill: "000000",
    type: ShadingType.SOLID,
  },
};
const emptyRow = new TableRow({
  children: [
    new TableCell({
      borders: noBorders,
      children: [new Paragraph("")],
    }),
  ],
});
const generateTable = (tableContent: ParsedTableData | null) => {
  if (tableContent) {
    const headersCells: TableCell[] = [];
    const valueRows: TableRow[] = [];
    tableContent.headers.map((header) =>
      headersCells.push(
        new TableCell({
          ...tableCellStyling,

          children: [
            new Paragraph({
              text: sanitizeHeaderText(header),
              style: "tableHeader",
              spacing: {
                after: 100,
              },
            }),
          ],
        })
      )
    );

    tableContent.values.forEach((valArr: any) => {
      const cells: TableCell[] = [];
      valArr.forEach((val: ItableCntentProps) => {
        cells.push(
          new TableCell({
            ...tableCellStyling,
            children: [new Paragraph({ text: `${val.val}`, style: "summary" })],
          })
        );
      });
      valueRows.push(
        new TableRow({
          children: cells
        })
      );
    });

    return new Table({
      width: {
        size: 100,
        type: WidthType.PERCENTAGE
      },
      layout: TableLayoutType.AUTOFIT,
      rows: [
        // Header
        new TableRow({
          tableHeader: true,
          children: headersCells
        }),
        ...valueRows
      ]
    });
  } else {
    return new Table({
      rows: [emptyRow]
    });
  }
};

const generateSectorTable = (tableContent: any, source: string) => {
  if (tableContent?.length && tableContent?.length > 0) {
    const headersCells: TableCell[] = [];
    const valueRows: TableRow[] = [];

    headersCells.push(
      new TableCell({
        ...tableCellStyling,

        children: [
          new Paragraph({
            text: sanitizeHeaderText(
              source === 'MarketReports' ? 'Document Title' : 'Relevant Document(s)'
            ),
            style: 'tableHeader',
            spacing: {
              after: 100
            }
          })
        ]
      }),
      new TableCell({
        ...tableCellStyling,

        children: [
          new Paragraph({
            text: sanitizeHeaderText(source === 'MarketReports' ? 'Page nos.' : 'Author(s)'),
            style: 'tableHeader',
            spacing: {
              after: 100
            }
          })
        ]
      })
    );
    if (source === 'MarketReports') {
      headersCells.push(
        new TableCell({
          ...tableCellStyling,
          children: [
            new Paragraph({
              text: sanitizeHeaderText('Source'),
              style: 'tableHeader',
              spacing: {
                after: 100
              }
            })
          ]
        })
      );
    }
    tableContent.forEach((data: any) => {
      const cells: TableCell[] = [];
      let fileNameContent = '';
      let fileParts = '';
      let pageNo = '';
      if (data.page_number.length > 0) {
        fileNameContent = data.file_name;
        fileParts = data.page_number.length > 1 ? '(Slides ' : '(Slide ';
      }
      data.page_number.length &&
        data.page_number.forEach((numer: any, i: number) => {
          fileParts = fileParts + numer + (i === data.page_number.length - 1 ? '' : ', ');
          pageNo = pageNo + numer + (i === data.page_number.length - 1 ? '' : ', ');
        });
      fileParts = fileParts + ')';

      cells.push(
        new TableCell({
          ...tableCellStyling,
          children: [
            new Paragraph({ text: fileNameContent }),
            new Paragraph({ text: source === 'MarketReports' ? '' : fileParts })
          ]
        })
      );
      if (source === 'MarketReports') {
        cells.push(
          new TableCell({
            ...tableCellStyling,
            children: [new Paragraph({ text: pageNo })]
          })
        );
      }
      let authorData: string = data.authors.reduce((str: any, author: any) => {
        if (str === '') {
          str = author?.author_name;
        } else {
          str = str + ', ' + author?.author_name;
        }
        return str;
      }, '');
      cells.push(
        new TableCell({
          ...tableCellStyling,
          children: [
            new Paragraph({
              text: source === 'MarketReports' ? data?.source : authorData
            })
          ]
        })
      );
      valueRows.push(
        new TableRow({
          children: cells
        })
      );
    });

    return new Table({
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },
      layout: TableLayoutType.AUTOFIT,
      rows: [
        // Header
        new TableRow({
          tableHeader: true,
          children: headersCells,
        }),
        ...valueRows,
      ],
    });
  } else {
    return new Table({
      rows: [emptyRow],
    });
  }
};

const seTokenError = (replyData: string) => {
  if (
    replyData?.includes(LABELS.TOKEN_ERR) ||
    replyData?.includes("https://aka.ms/oai/quotaincrease") ||
    replyData?.includes("SQL_ERROR")
  ) {
    return true;
  } else {
    return false;
  }
};

const getSources = (values: { Sources: any; summary: string }) => {
  if (values?.Sources || values.summary?.includes("Information_Sources:")) {
    let sourcesFromChat: string = "";
    if (values.summary?.includes("Information_Sources:")) {
      const dataSplitted = values.summary?.split("Information_Sources:");
      values.summary = dataSplitted[0] as any;
      sourcesFromChat = dataSplitted[1];
    }
    return getSourcesList(sourcesFromChat, values?.Sources || []);
  }
};

const handleSourceSection = (
  sourceArray: string[],
  checkedCheckboxes?: any,
  eyIPInterRadio?: any,
  validURLinputValue?: any
) => {
  let sourceType = '';
  if (checkedCheckboxes && checkedCheckboxes.length > 0) {
    if (checkedCheckboxes.length === 1 && checkedCheckboxes.includes(publicSources[0]))
      sourceType = publicSources[0];
    else if (checkedCheckboxes.length === 1 && checkedCheckboxes.includes(publicSources[1]))
      sourceType = publicSources[1];
    else if (checkedCheckboxes.length === 1 && checkedCheckboxes.includes(publicSources[2]))
      sourceType = publicSources[2];
    else if (
      checkedCheckboxes.length === 2 &&
      checkedCheckboxes.includes(publicSources[0]) &&
      checkedCheckboxes.includes(publicSources[1])
    )
      sourceType = 'Combined';
  }
  if (eyIPInterRadio) {
    sourceType = eyIPInterRadio;
  }
  let modifiedSourceArray;
  if(sourceType==='Surveys'){
    if(sourceArray?.length>0 && sourceArray[0].includes('Surveys')) {
      modifiedSourceArray = ['EY conducted surveys'];
     }
     else{
       modifiedSourceArray = [...sourceArray];
     }
  }
  else{
  let getSources = validURLinputValue ? validURLinputValue : getResponseSourceType(sourceType);
  sourceArray.shift();
  modifiedSourceArray = [getSources, ...sourceArray];
  }
  if (modifiedSourceArray && modifiedSourceArray.length > 0) {
    return modifiedSourceArray;
  }
  return 'Secondary research';
};

const generateDocSummary = (
  line: string,
  type: string,
  totalLength: number = 0,
  ind: number,
  isParitallyAbortedResponse: boolean,
  isCompletelyAbortedResposne: boolean,
  sourceSection?: any,
  source?: any,
) => {
  let modifiedSource: any = [];
  const pathName = window.location.pathname;
  sourceSection.map((source: any, index: number) => {
    if (isParitallyAbortedResponse && index === 0) {
      modifiedSource.push(new TextRun({
        text: LABELS.chat_reply.PARTIAL_STOP_GENERATION_MSG,
        break: 1,
        color: '656579',
        italics: true
      }))
    }
    if(pathName=="/surveyChatPage") {
      if(!sourceSection[0] || sourceSection[0]?.trim()==='EY conducted surveys'){
        modifiedSource.push(new TextRun({
          text: index === 0 ? `Source: ${source}` : `${source}`,
          break: 1
        })
        )
      }
      else
     {
      modifiedSource.push(new TextRun({
        text: index === 0 ? `Source: ${source}` : `${source}`,
        break: 1
      })
      )
     }
    }
    else{
    modifiedSource.push(new TextRun({
      text: index === 0 ? `Source: ${source}` : `${source}`,
      // break: 1
    })
    );
  }
  });

  if (seTokenError(line)) {
    return new Paragraph({
      children: [
        new TextRun({ text: LABELS.chat_reply.EMBROYNIC_ERROR }),
        new TextRun({ text: '', break: 1 }),
        ...modifiedSource
      ],
    });
  }

  if (RESPONSE_BY.STRATEGY_EDGE === type && totalLength - 1 === ind) {
    return new Paragraph({
      children: [
        new TextRun({ text: line, color: isCompletelyAbortedResposne ? '656579' : '', italics: isCompletelyAbortedResposne ? true : false  }),
        new TextRun({ text: '' , break: 1}),
        ...modifiedSource
      ]
    });
  }
  if (RESPONSE_BY.SECTOR === type && totalLength - 1 === ind) {
    return new Paragraph({
      children: [
        new TextRun({ text: line }),
        ...modifiedSource,
        new TextRun({ text: '\n', break: 1 })
      ]
    });
  }
  if (RESPONSE_BY.PESPECTIVE === type && totalLength - 1 === ind) {
    return new Paragraph({
      children: [
        new TextRun({ text: line }),
        ...modifiedSource,
        new TextRun({ text: '\n', break: 1 })
      ]
    });
  }
  if (line.startsWith(LABELS.SORRY_ERR) && !line.startsWith(LABELS.SORRY_ERR1)) {
    return new Paragraph({
      children: [
        new TextRun({ text: LABELS.GENERIC_OOPS_ERROR }),
        new TextRun({
          text: ' Source: Secondary research',
          break: 1
        })
      ]
    });
  }

  if (RESPONSE_BY.OPENAI === type && line?.includes('Information_Sources')) {
    return new Paragraph(line?.replace('Information_Sources', 'Source: Secondary research'));
  }

  if (RESPONSE_BY.OPENAI === type && line?.includes('Oops! No results found')) {
    return new Paragraph({
      children: [
        new TextRun({ text: line }),
        new TextRun({
          text: 'Source: Secondary research',
          break: 1
        })
      ]
    });
  }
  return new Paragraph(line);
};

const generateDocument = async (
  data: any,
  userImg: string | undefined,
  source: any,
  checkedCheckboxes?: any,
  eyIPInterRadio?: any,
  validURLinputValue?: any
) => {
  const IMAGES = {
    [RESPONSE_BY.USER]: userImg ? userImg : AppConst.AVATAR_IMAGE,
    [RESPONSE_BY.STRATEGY_EDGE]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.EMBAI]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.EMBROYNIC]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.OPENAI]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.SECTOR]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.SURVEY]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.PESPECTIVE]: AppConst.USER_AI_IMG,
    [RESPONSE_BY.MARKET_REPORT]: AppConst.USER_AI_IMG
  };
  const paragraphs: any = [];

  const tableRowWithCol = (
    respData: any,
    respImg: any,
    tableContent?: any,
    respType?: any,
    sourceSection?: any
  ) => {
    const totalLength = decodeHTML(respData.summary).split("\n");
    const { length } = totalLength;
    return (
      paragraphs.push(
        new TableRow({
          children: [
            new TableCell({
              borders: noBorders,
              margins: {
                right: 200,
              },
              children: [
                new Paragraph({
                  children: [
                    new ImageRun({
                      data: respImg,
                      transformation: {
                        width: 30,
                        height: 30,
                      },
                    }),
                  ],
                }),
              ],
            }),

            new TableCell({
              borders: noBorders,
              children: [
                ...(respType !== 'user'
                  ? decodeHTML(respData.summary)
                      .replaceAll('#', '')
                      .replaceAll('*', '')
                      .split('\n')
                      .map((line: string, ind: number) => {
                        return generateDocSummary(
                          line,
                          respType,
                          length,
                          ind,
                          respData?.isParitallyAbortedResponse,
                          respData?.isCompletelyAbortedResposne,
                          sourceSection
                        );
                      })
                  : [new Paragraph(respData.summary?.replace(/\n\r+$/, '').trim())]),
                source !== 'SectorInsights' &&
                source !== 'Perspective' &&
                source !== 'MarketReports'
                  ? generateTable(tableContent)
                  : generateSectorTable(tableContent, source)
              ]
            })
          ]
        })
      ),
      paragraphs.push(emptyRow)
    );
  };

  data.forEach(async (chat: any, ind: number) => {
    const { SEResponse, LLMResponse, user } = chat;
    let tableContent = null;
    let sources: string[] | undefined = [];
    const sourceSection =
      SEResponse &&
      handleSourceSection(
        SEResponse?.Sources,
        checkedCheckboxes,
        eyIPInterRadio,
        validURLinputValue
      );
    if (
      SEResponse?.ResponseType === RESPONSE_TYPE.TABLE &&
      SEResponse.Content &&
      SEResponse.Content.every((el: any) => Boolean(el) && typeof el === 'object')
    ) {
      tableContent = prepareContentForTable(SEResponse?.Content || LLMResponse?.Content);
    } else if (
      SEResponse?.relevantFiles &&
      SEResponse?.relevantFiles.every((el: any) => Boolean(el) && typeof el === 'object')
    ) {
      tableContent = SEResponse?.relevantFiles?.map((el: any) => {
        const obj: any = {
          file_name: el.file_name ?? '',
          page_number: el.page_number || [],
          authors: el?.authors || [],
          sector: el?.sector || [],
          geography: el?.geography || [],
          contentAge: el?.contentAge || '',
          source: el?.source || ''
        };
        return obj;
      });
    }
    if (SEResponse || LLMResponse) {
      sources = getSources(SEResponse || LLMResponse);
    }

    if (user) {
      tableRowWithCol(user, IMAGES[RESPONSE_BY.USER], null, RESPONSE_BY.USER, sourceSection);
    }
    if (SEResponse) {
      if (!SEResponse.relevantFiles && source === 'Emb') {
        tableRowWithCol(
          SEResponse,
          IMAGES[RESPONSE_BY.EMBROYNIC],
          tableContent,
          RESPONSE_BY.STRATEGY_EDGE,
          sourceSection
        );
      } else if(source === 'EmbOpenAI'){
        tableRowWithCol(
          SEResponse,
          IMAGES[RESPONSE_BY.EMBAI],
          tableContent,
          RESPONSE_BY.STRATEGY_EDGE,
          sourceSection
        );
      }
      else if(source === 'OpenAI'){
        tableRowWithCol(
          SEResponse,
          IMAGES[RESPONSE_BY.OPENAI],
          tableContent,
          RESPONSE_BY.STRATEGY_EDGE,
          sourceSection
        );
      }
      else if(source === 'SurveyInsights'){
        tableRowWithCol(
          SEResponse,
          IMAGES[RESPONSE_BY.SURVEY],
          tableContent,
          RESPONSE_BY.STRATEGY_EDGE,
          sourceSection
        );
      } else if (source === 'MarketReports') {
        tableRowWithCol(
          SEResponse,
          IMAGES[RESPONSE_BY.MARKET_REPORT],
          tableContent,
          RESPONSE_BY.STRATEGY_EDGE,
          sourceSection
        );
      } else {
        tableRowWithCol(
          SEResponse,
          IMAGES[RESPONSE_BY.SECTOR],
          tableContent,
          RESPONSE_BY.SECTOR,
          sourceSection
        );
      }
    }
  });

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
            size: "11pt",
            font: "Calibri",
          },
        },
      },
      paragraphStyles: [
        {
          id: "tableHeader",
          run: {
            bold: true,
          },
        },
        {
          id: "summary",
          quickFormat: true,
        },
      ],
    },
    sections: [
      {
        children: [
          new Table({
            rows: paragraphs,
          }),
        ],
      },
    ],
  });
  return doc;
};

const exportToWord = async (
  data: IQuery[],
  userImage?: string,
  source?: any,
  checkedCheckboxes?: any,
  eyIPInterRadio?: any,
  validURLinputValue?: any
) => {
  const clonedData = structuredClone(data);
  const doc = await generateDocument(
    clonedData,
    userImage,
    source,
    checkedCheckboxes,
    eyIPInterRadio,
    validURLinputValue
  );
  const blob = await Packer.toBlob(doc);
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = downloadFilenameGenerator(source, 'docx');
  link.click();
};

export default exportToWord;
